import { useState } from "react";
import { Modal, DatePicker, Input } from "antd";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { markAbsent } from "../services/attendanceService";

/**
 * Mark an employee absent for a date range.
 *
 * "4 days off from today" is from = today, to = today + 3 — the range is
 * inclusive on both ends. Round-robin skips them for every day in it, and they
 * come back automatically when it ends.
 */
const MarkAbsentModal = ({ member, onClose, onDone }) => {
  const [from, setFrom] = useState(dayjs());
  const [to, setTo] = useState(dayjs());
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    const next = {};
    if (!from) next.from = "Pick a start date";
    if (!to) next.to = "Pick an end date";
    // The API answers 422 for this; catching it here is friendlier.
    if (from && to && to.isBefore(from, "day")) next.to = "The end date cannot be before the start date";
    setErrors(next);
    if (Object.keys(next).length) return;

    setSaving(true);
    try {
      await markAbsent(member.id, {
        from_date: from.format("YYYY-MM-DD"),
        to_date: to.format("YYYY-MM-DD"),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      const days = to.diff(from, "day") + 1;
      toast.success(
        `${member.name} marked absent for ${days} day${days === 1 ? "" : "s"}.`,
      );
      if (typeof onDone === "function") onDone();
      onClose();
    } catch (err) {
      toast.error(err?.message || "Could not mark this employee absent");
    } finally {
      setSaving(false);
    }
  };

  const label = { fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6 };
  const errStyle = { color: "#dc2626", fontSize: 12, marginTop: 5 };

  return (
    <Modal
      open
      title={<span style={{ fontWeight: 700 }}>Mark absent</span>}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={saving ? "Saving…" : "Mark absent"}
      okButtonProps={{ loading: saving, danger: true }}
      cancelButtonProps={{ disabled: saving }}
      destroyOnClose
      width={440}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 4 }}>
        <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{member?.name || "—"}</div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{member?.email || ""}</div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <label style={label}>From <span style={{ color: "#dc2626" }}>*</span></label>
            <DatePicker
              value={from}
              onChange={(v) => {
                setFrom(v);
                setErrors((p) => ({ ...p, from: "", to: "" }));
                // Keep the range valid instead of letting it submit into a 422.
                if (v && to && to.isBefore(v, "day")) setTo(v);
              }}
              format="DD-MM-YYYY"
              style={{ width: "100%" }}
              allowClear={false}
            />
            {errors.from && <div style={errStyle}>{errors.from}</div>}
          </div>
          <div>
            <label style={label}>To <span style={{ color: "#dc2626" }}>*</span></label>
            <DatePicker
              value={to}
              onChange={(v) => { setTo(v); setErrors((p) => ({ ...p, to: "" })); }}
              format="DD-MM-YYYY"
              style={{ width: "100%" }}
              allowClear={false}
              disabledDate={(d) => from && d && d.isBefore(from, "day")}
            />
            {errors.to && <div style={errStyle}>{errors.to}</div>}
          </div>
        </div>

        <div>
          <label style={label}>Reason</label>
          <Input.TextArea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="e.g. On leave (optional)"
          />
        </div>

        <div style={{ fontSize: 11.5, color: "#6b7280", lineHeight: 1.5 }}>
          New leads skip this employee for the whole range. They are available
          again automatically once it ends.
        </div>
      </div>
    </Modal>
  );
};

export default MarkAbsentModal;
